// src/pages/prodactions/master-data/product-specifications/ProductSpecifications.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TablePagination,
    IconButton,
    Stack,
    Snackbar,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

import { useTheme } from "@mui/material/styles";
import '../../../../components/css/ded-style.css';
import useAuthCheck from "../../../../hooks/useAuthCheck";
import { useOutletContext } from "react-router-dom";
import { database } from "../../../../firebase";
import { ref, onValue, set, remove, push } from "firebase/database";
import * as XLSX from "xlsx";

// Import formatters dari file utilitas
import {
    formatNumber,
    formatDecimal,
    formatText
} from "../../../../utils/formatters-master";

// Import fungsi formatLastUpdated dari file terpisah
import { formatLastUpdated } from "../../../../utils/formatLastUpdated";

import CustomBreadcrumbs from "../../../../components/universal-features/breadcrumbs";

// Import CustomTableContainer dari komponen terpisah
import CustomTableContainer from "../../../../components/CustomScrollContainer";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

function ProductSpecifications() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const fileInputRef = useRef(null);
    const [importLoading, setImportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [editingRow, setEditingRow] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [lastUpdated, setLastUpdated] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newData, setNewData] = useState({
        product_specification_id: '',
        product_specification_name: '',
        description: '',
        status: 'Active'
    });

    // State untuk animasi rotasi icon Plus/X
    const [isAddIconRotated, setIsAddIconRotated] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                // Fetch main data from db_productions/tb_product_specifications
                const specificationsRef = ref(database, "db_productions/tb_product_specifications");
                const specificationsSnapshot = await onValuePromise(specificationsRef);
                const specifications = specificationsSnapshot.val();
                const loadedData = [];
                let latestUpdateTime = null;
                
                if (specifications) {
                    Object.keys(specifications).forEach(key => {
                        const record = specifications[key];
                        loadedData.push({ 
                            firebaseKey: key, 
                            product_specification_id: record.product_specification_id || key, // Jika ID kosong, gunakan firebase key
                            product_specification_name: record.product_specification_name || '',
                            description: record.description || '',
                            status: record.status || 'Active',
                            last_updated: record.last_updated || null
                        });
                        
                        // Cari waktu update terakhir
                        if (record.last_updated) {
                            const updateTime = new Date(record.last_updated);
                            if (!latestUpdateTime || updateTime > latestUpdateTime) {
                                latestUpdateTime = updateTime;
                            }
                        }
                    });
                    
                    // Set last updated time
                    if (latestUpdateTime) {
                        setLastUpdated(latestUpdateTime);
                    }
                }

                // Sort by product_specification_id
                loadedData.sort((a, b) => a.product_specification_id.localeCompare(b.product_specification_id));
                
                setOriginalDataList(loadedData);
                setDataList(loadedData);

            } catch (error) {
                console.error("Error fetching data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data product specifications.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchData();
        }
    }, [user]);

    // Effect untuk melakukan pencarian saat searchQuery berubah
    useEffect(() => {
        if (searchQuery) {
            const filtered = originalDataList.filter(item => 
                item.product_specification_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.product_specification_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.status.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setDataList(filtered);
        } else {
            setDataList(originalDataList);
        }
        setPage(0);
    }, [searchQuery, originalDataList]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Fungsi import Excel
    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            setSnackbar({
                open: true,
                message: "Tidak ada file yang dipilih.",
                severity: "warning",
            });
            return;
        }

        setImportLoading(true);
        setSnackbar({
            open: true,
            message: "Memulai proses import...",
            severity: "info",
        });

        try {
            // Baca file Excel
            const reader = new FileReader();
            
            reader.onload = async (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });

                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    
                    // Path database
                    const dbPath = "db_productions/tb_product_specifications";
                    const tbRef = ref(database, dbPath);

                    // Ambil data yang sudah ada
                    const snapshot = await onValuePromise(tbRef);
                    const existingData = snapshot.exists() ? snapshot.val() : {};

                    let importedCount = 0;
                    let updatedCount = 0;
                    let ignoredCount = 0;
                    const errors = [];

                    // Gunakan data dengan default settings
                    const jsonData = XLSX.utils.sheet_to_json(worksheet);
                    const dataToProcess = jsonData;

                    for (const item of dataToProcess) {
                        try {
                            // Cari semua kemungkinan nama kolom
                            let specificationId = "";
                            let specificationName = "";
                            let description = "";
                            let status = "Active"; // Default status

                            // Cari berdasarkan berbagai kemungkinan nama kolom
                            if (typeof item === 'object') {
                                specificationId = item["Specification ID"] || item["product_specification_id"] || item["Kode Spesifikasi"] || 
                                               item["SpecificationId"] || item["specificationid"] || item["SPECIFICATION ID"] || "";
                                
                                specificationName = item["Specification Name"] || item["product_specification_name"] || item["Nama Spesifikasi"] || 
                                                 item["SpecificationName"] || item["specificationname"] || item["SPECIFICATION NAME"] || "";
                                
                                description = item["Description"] || item["description"] || item["Deskripsi"] || 
                                            item["DESCRIPTION"] || "";
                                
                                const importedStatus = item["Status"] || item["status"] || item["STATUS"] || "";
                                if (importedStatus && importedStatus.toString().trim() !== "") {
                                    status = importedStatus.toString().trim();
                                }
                            }

                            // Persiapkan data untuk disimpan
                            const dataToSave = {
                                product_specification_name: specificationName.toString().trim(),
                                description: description.toString().trim(),
                                status: status,
                                last_updated: new Date().toISOString()
                            };

                            // Jika specification_id diisi, tambahkan ke data
                            if (specificationId && specificationId.toString().trim() !== "") {
                                dataToSave.product_specification_id = specificationId.toString().trim();
                            }

                            // Cek apakah data sudah ada berdasarkan product_specification_id atau firebaseKey
                            let existingKey = null;
                            
                            if (specificationId && specificationId.toString().trim() !== "") {
                                const specificationIdLower = specificationId.toString().toLowerCase().trim();
                                existingKey = Object.keys(existingData).find(key => 
                                    existingData[key].product_specification_id?.toLowerCase().trim() === specificationIdLower
                                );
                            }

                            if (existingKey) {
                                // Update data yang sudah ada
                                const existingItemRef = ref(database, `${dbPath}/${existingKey}`);
                                await set(existingItemRef, {
                                    ...dataToSave,
                                    product_specification_id: existingData[existingKey].product_specification_id || existingKey
                                });
                                updatedCount++;
                            } else {
                                // Simpan sebagai data baru
                                // Buat key baru menggunakan push
                                const newKeyRef = push(tbRef);
                                const newKey = newKeyRef.key;
                                
                                const newDataToSave = {
                                    ...dataToSave,
                                    product_specification_id: dataToSave.product_specification_id || newKey
                                };
                                
                                await set(newKeyRef, newDataToSave);
                                importedCount++;
                            }

                        } catch (error) {
                            console.error("Error processing row:", error);
                            errors.push(`Error pada baris: ${JSON.stringify(item)} - ${error.message}`);
                            ignoredCount++;
                        }
                    }

                    // Refresh data setelah import
                    await refreshData();

                    // Tampilkan hasil import
                    let message = `Import selesai! ${importedCount} data baru, ${updatedCount} data diperbarui.`;
                    let severity = "success";
                    
                    if (ignoredCount > 0) {
                        message += ` ${ignoredCount} baris diabaikan (error).`;
                        severity = "warning";
                    }
                    
                    if (errors.length > 0) {
                        console.error("Import errors:", errors);
                    }

                    setSnackbar({
                        open: true,
                        message: message,
                        severity: severity,
                    });

                } catch (error) {
                    console.error("Gagal mengimpor Excel:", error);
                    setSnackbar({
                        open: true,
                        message: `Gagal membaca file Excel: ${error.message}`,
                        severity: "error",
                    });
                } finally {
                    setImportLoading(false);
                }
            };

            reader.onerror = (error) => {
                console.error("Error reading file:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal membaca file.",
                    severity: "error",
                });
                setImportLoading(false);
            };

            reader.readAsArrayBuffer(file);

        } catch (error) {
            console.error("Error in import process:", error);
            setSnackbar({
                open: true,
                message: "Terjadi kesalahan saat memproses file.",
                severity: "error",
            });
            setImportLoading(false);
        } finally {
            event.target.value = null;
        }
    };

    // Fungsi untuk refresh data dari Firebase
    const refreshData = async () => {
        try {
            setLoadingData(true);
            const specificationsRef = ref(database, "db_productions/tb_product_specifications");
            const specificationsSnapshot = await onValuePromise(specificationsRef);
            const specifications = specificationsSnapshot.val();
            const loadedData = [];
            let latestUpdateTime = null;
            
            if (specifications) {
                Object.keys(specifications).forEach(key => {
                    const record = specifications[key];
                    loadedData.push({ 
                        firebaseKey: key, 
                        product_specification_id: record.product_specification_id || key,
                        product_specification_name: record.product_specification_name || '',
                        description: record.description || '',
                        status: record.status || 'Active',
                        last_updated: record.last_updated || null
                    });
                    
                    if (record.last_updated) {
                        const updateTime = new Date(record.last_updated);
                        if (!latestUpdateTime || updateTime > latestUpdateTime) {
                            latestUpdateTime = updateTime;
                        }
                    }
                });
                
                if (latestUpdateTime) {
                    setLastUpdated(latestUpdateTime);
                }
            }

            loadedData.sort((a, b) => a.product_specification_id.localeCompare(b.product_specification_id));
            
            setOriginalDataList(loadedData);
            setDataList(loadedData);
            
        } catch (error) {
            console.error("Error refreshing data:", error);
            throw error;
        } finally {
            setLoadingData(false);
        }
    };

    const handleExport = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Export akan dikembangkan.",
            severity: "info",
        });
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print akan dikembangkan.",
            severity: "info",
        });
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleEditClick = (row) => {
        setEditingRow(row.firebaseKey);
        setEditFormData({
            product_specification_id: row.product_specification_id,
            product_specification_name: row.product_specification_name,
            description: row.description,
            status: row.status
        });
    };

    const handleEditChange = (field, value) => {
        setEditFormData({
            ...editFormData,
            [field]: value
        });
    };

    const handleSaveEdit = async (firebaseKey) => {
        try {
            // Update data di Firebase
            const dataRef = ref(database, `db_productions/tb_product_specifications/${firebaseKey}`);
            const updatedData = {
                product_specification_id: editFormData.product_specification_id || firebaseKey,
                product_specification_name: editFormData.product_specification_name,
                description: editFormData.description,
                status: editFormData.status,
                last_updated: new Date().toISOString()
            };
            
            await set(dataRef, updatedData);
            
            // Update local state
            const updatedList = dataList.map(item => {
                if (item.firebaseKey === firebaseKey) {
                    return {
                        ...item,
                        ...updatedData,
                        last_updated: updatedData.last_updated
                    };
                }
                return item;
            });
            
            setDataList(updatedList);
            setOriginalDataList(updatedList);
            setEditingRow(null);
            
            setSnackbar({
                open: true,
                message: "Data berhasil diperbarui.",
                severity: "success",
            });
            
            // Update last updated time
            setLastUpdated(new Date());
            
        } catch (error) {
            console.error("Error updating data:", error);
            setSnackbar({
                open: true,
                message: "Gagal memperbarui data.",
                severity: "error",
            });
        }
    };

    const handleCancelEdit = () => {
        setEditingRow(null);
        setEditFormData({});
    };

    const handleDelete = async (firebaseKey, specificationId) => {
        if (window.confirm(`Apakah Anda yakin ingin menghapus data dengan Specification ID: ${specificationId}?`)) {
            try {
                // Hapus data dari Firebase
                const dataRef = ref(database, `db_productions/tb_product_specifications/${firebaseKey}`);
                await remove(dataRef);
                
                // Update local state
                const updatedList = dataList.filter(item => item.firebaseKey !== firebaseKey);
                setDataList(updatedList);
                setOriginalDataList(updatedList);
                
                setSnackbar({
                    open: true,
                    message: "Data berhasil dihapus.",
                    severity: "success",
                });
                
                // Update last updated time
                setLastUpdated(new Date());
                
            } catch (error) {
                console.error("Error deleting data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal menghapus data.",
                    severity: "error",
                });
            }
        }
    };

    const handleAddNew = () => {
        setIsAddIconRotated(!isAddIconRotated);
        setShowAddForm(!showAddForm);
        // Reset form dengan status default Active
        if (!showAddForm) {
            setNewData({
                product_specification_id: '',
                product_specification_name: '',
                description: '',
                status: 'Active'
            });
        }
    };

    const handleNewDataChange = (field, value) => {
        setNewData({
            ...newData,
            [field]: value
        });
    };

    const handleSaveNew = async () => {
        try {
            // Buat key baru menggunakan push
            const specificationsRef = ref(database, "db_productions/tb_product_specifications");
            const newKeyRef = push(specificationsRef);
            const newKey = newKeyRef.key;
            
            // Persiapkan data untuk disimpan
            const dataToSave = {
                product_specification_id: newData.product_specification_id.trim() || newKey,
                product_specification_name: newData.product_specification_name.trim(),
                description: newData.description.trim(),
                status: newData.status,
                last_updated: new Date().toISOString()
            };
            
            await set(newKeyRef, dataToSave);
            
            // Update local state
            const newItem = {
                firebaseKey: newKey,
                ...dataToSave
            };
            
            const updatedList = [...originalDataList, newItem].sort((a, b) => 
                a.product_specification_id.localeCompare(b.product_specification_id)
            );
            
            setDataList(updatedList);
            setOriginalDataList(updatedList);
            
            // Reset form dan state
            setNewData({
                product_specification_id: '',
                product_specification_name: '',
                description: '',
                status: 'Active'
            });
            setShowAddForm(false);
            setIsAddIconRotated(false);
            
            setSnackbar({
                open: true,
                message: "Data berhasil ditambahkan.",
                severity: "success",
            });
            
            // Update last updated time
            setLastUpdated(new Date());
            
        } catch (error) {
            console.error("Error adding data:", error);
            setSnackbar({
                open: true,
                message: "Gagal menambahkan data.",
                severity: "error",
            });
        }
    };

    const handleCancelNew = () => {
        setShowAddForm(false);
        setIsAddIconRotated(false);
        setNewData({
            product_specification_id: '',
            product_specification_name: '',
            description: '',
            status: 'Active'
        });
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Product Specifications
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Cari..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            sx={{ width: 250 }}
                        />
                        {/* IconButton Plus/X untuk Tambah Data */}
                        <IconButton 
                            color="primary" 
                            size="small" 
                            onClick={handleAddNew}
                            sx={{
                                transition: 'transform 0.3s ease-in-out',
                                transform: isAddIconRotated ? 'rotate(45deg)' : 'rotate(0deg)',
                                backgroundColor: isAddIconRotated ? '#f2f9ff' : 'transparent',
                                '&:hover': {
                                    backgroundColor: isAddIconRotated ? '#f2f9ff' : 'action.hover'
                                }
                            }}
                        >
                            <AddOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handleImportButtonClick} disabled={importLoading}>
                            {importLoading ? <CircularProgress size={20} /> : <FileUploadOutlinedIcon />}
                        </IconButton>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleImportExcel}
                        />
                        <IconButton color="primary" size="small" onClick={handleExport}>
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handlePrint}>
                            <PrintOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                {/* Form Tambah Data Baru */}
                {showAddForm && (
                    <Paper sx={{ p: 2, mb: 2, bgcolor: '#f2f9ff' }} elevation={0}>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                            Tambah Data Baru
                        </Typography>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                            <TextField
                                label="Specification ID (Opsional)"
                                size="small"
                                value={newData.product_specification_id}
                                onChange={(e) => handleNewDataChange('product_specification_id', e.target.value)}
                                placeholder="Kosongkan untuk isi otomatis"
                                fullWidth
                                helperText="Jika dikosongkan akan diisi otomatis"
                            />
                            <TextField
                                label="Specification Name"
                                size="small"
                                value={newData.product_specification_name}
                                onChange={(e) => handleNewDataChange('product_specification_name', e.target.value)}
                                fullWidth
                                required
                            />
                            <TextField
                                label="Description"
                                size="small"
                                value={newData.description}
                                onChange={(e) => handleNewDataChange('description', e.target.value)}
                                fullWidth
                            />
                            <FormControl fullWidth size="small">
                                <InputLabel id="status-select-label">Status</InputLabel>
                                <Select
                                    labelId="status-select-label"
                                    id="status-select"
                                    value={newData.status}
                                    label="Status"
                                    onChange={(e) => handleNewDataChange('status', e.target.value)}
                                >
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="Nonactive">Nonactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button variant="outlined" onClick={handleCancelNew}>
                                Batal
                            </Button>
                            <Button variant="contained" onClick={handleSaveNew}>
                                Simpan
                            </Button>
                        </Stack>
                    </Paper>
                )}

                {displayedDataList.length > 0 ? (
                    <>
                        {/* Gunakan CustomTableContainer di sini */}
                        <CustomTableContainer
                            sx={{ 
                                maxHeight: 500,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1
                            }}
                            hoverScrollbar={true}
                            scrollbarHeight="8px"
                            scrollbarWidth="8px"
                            scrollbarThumbColor="#c1c1c1"
                            scrollbarHoverColor="#a8a8a8"
                        >
                            <Table stickyHeader aria-label="product specifications table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="ded-tch" style={{ width: '50px' }}>No</TableCell>
                                        <TableCell className="ded-tch">Specification Name</TableCell>
                                        <TableCell className="ded-tch">Description</TableCell>
                                        <TableCell className="ded-tch">Status</TableCell>
                                        <TableCell className="ded-tch" style={{ width: '100px' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {displayedDataList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row, index) => (
                                            <TableRow key={row.firebaseKey}>
                                                {editingRow === row.firebaseKey ? (
                                                    <>
                                                        <TableCell>
                                                            {page * rowsPerPage + index + 1}
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.product_specification_name}
                                                                onChange={(e) => handleEditChange('product_specification_name', e.target.value)}
                                                                fullWidth
                                                                required
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.description}
                                                                onChange={(e) => handleEditChange('description', e.target.value)}
                                                                fullWidth
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <FormControl fullWidth size="small">
                                                                <Select
                                                                    value={editFormData.status || 'Active'}
                                                                    onChange={(e) => handleEditChange('status', e.target.value)}
                                                                >
                                                                    <MenuItem value="Active">Active</MenuItem>
                                                                    <MenuItem value="Nonactive">Nonactive</MenuItem>
                                                                </Select>
                                                            </FormControl>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1}>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="success"
                                                                    onClick={() => handleSaveEdit(row.firebaseKey)}
                                                                >
                                                                    <CheckOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="error"
                                                                    onClick={handleCancelEdit}
                                                                >
                                                                    <CloseOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </>
                                                ) : (
                                                    <>
                                                        <TableCell>
                                                            {page * rowsPerPage + index + 1}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box>
                                                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                                    {formatText(row.product_specification_name)}
                                                                </div>
                                                                <div style={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                                                    ID: {formatText(row.product_specification_id)}
                                                                </div>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>{formatText(row.description)}</TableCell>
                                                        <TableCell>
                                                            <Box sx={{ 
                                                                display: 'inline-block',
                                                                px: 1.5,
                                                                py: 0.5,
                                                                borderRadius: 1,
                                                                backgroundColor: row.status === 'Active' ? '#e8f5e9' : '#ffebee',
                                                                color: row.status === 'Active' ? '#2e7d32' : '#c62828',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                {formatText(row.status)}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1}>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="primary"
                                                                    onClick={() => handleEditClick(row)}
                                                                >
                                                                    <EditOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="error"
                                                                    onClick={() => handleDelete(row.firebaseKey, row.product_specification_id)}
                                                                >
                                                                    <DeleteOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </CustomTableContainer>
                        
                        {/* Container untuk TablePagination dan Last Updated Info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            {/* Bagian kiri: Info Data diupdate terakhir */}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Data diupdate terakhir: {formatLastUpdated(lastUpdated)}
                            </Typography>
                            
                            {/* Bagian kanan: TablePagination */}
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={displayedDataList.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{ 
                                    flexShrink: 0,
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        margin: 0
                                    }
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 200,
                        border: '1px dashed #e0e0e0',
                        borderRadius: 1
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchQuery ? 'Tidak ada data yang sesuai dengan pencarian' : 'Tidak ada data product specifications. Klik icon "+" untuk menambahkan atau "Import Excel" untuk mengimpor data.'}
                        </Typography>
                    </Box>
                )}
            </Paper>
            
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default ProductSpecifications;